/**
 * DemoLimitPopup Component
 * Displays a full-screen overlay popup when demo limits are reached for free-tier users.
 * Follows the CelebrationPopup overlay pattern.
 *
 * US-005: Question and Attempt Limits with Auto-Complete
 * US-006: Demo Limit Notifications and User Feedback
 */

import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Reason variants for the demo limit popup
 */
export type DemoLimitReason = 'attempt_limit' | 'question_limit' | 'session_limit';

/**
 * Props for DemoLimitPopup component
 */
export interface DemoLimitPopupProps {
  /** Controls visibility of the popup */
  isVisible: boolean;
  /** The reason for showing the popup */
  reason: DemoLimitReason;
  /** Callback when popup is dismissed (for attempt_limit only) */
  onDismiss: () => void;
  /** Override for max attempts per question (fallback: 5) */
  maxAttemptsPerQuestion?: number;
  /** Override for max questions per session (fallback: 2) */
  maxQuestionsPerSession?: number;
  /** Override for max lifetime sessions (fallback: 2) */
  maxSessions?: number;
  /**
   * IMPL stage-escalation (attempt_limit variant only): persistence Pebbles awarded
   * for the exhausted question. Backend always awards these now (never 0).
   * Fallback 2 matches the config default persistence_pebbles=2.
   */
  pebblesAwarded?: number;
}

/**
 * Variant content for each limit reason.
 * attempt_limit (IMPL stage-escalation): an encouraging persistence message with
 * NO demo/limit framing anywhere (heading, subtitle, body). It fires only when the
 * student's final answer was WRONG, so the subtitle and body must say so outright:
 * praise-only copy left students believing their last answer had been accepted.
 * question_limit / session_limit keep the original PRD Section 6.1 demo copy
 * (dead code for premium flows, out of scope).
 * Accepts dynamic limit values with fallback defaults matching DEMO_LIMITS constants.
 */
function getVariantContent(limits: {
  maxAttemptsPerQuestion: number;
  maxQuestionsPerSession: number;
  maxSessions: number;
  pebblesAwarded: number;
}): Record<
  DemoLimitReason,
  {
    emoji: string;
    heading: string;
    subtitle: string | null;
    body: string;
    buttonLabel: string;
    navigateAway: boolean;
  }
> {
  return {
    attempt_limit: {
      emoji: '\u{1F31F}',
      heading: 'Amazing Persistence!',
      subtitle: 'Not quite right this time.',
      body: `You didn\u2019t get this one, but you kept trying, and that\u2019s what great learners do. The correct answer is in the chat. You earned ${limits.pebblesAwarded} Pebbles for persisting.`,
      buttonLabel: 'Continue',
      navigateAway: false,
    },
    question_limit: {
      emoji: '\u{1F393}',
      heading: 'Open Public Demo',
      subtitle: 'Thank you for trying out ThinkOtter!',
      body: `You\u2019ve completed all ${limits.maxQuestionsPerSession} demo questions \u2014 great job! Because this is an open public demo, sessions are limited to ${limits.maxQuestionsPerSession} questions.`,
      buttonLabel: 'Back to Sessions',
      navigateAway: true,
    },
    session_limit: {
      emoji: '\u{1F393}',
      heading: 'Open Public Demo',
      subtitle: 'Thank you for trying out ThinkOtter!',
      body: `You\u2019ve used all your available demo sessions. Because this is an open public demo, each user is allowed up to ${limits.maxSessions} sessions. Thank you for trying ThinkOtter!`,
      buttonLabel: 'Back to Sessions',
      navigateAway: true,
    },
  };
}

/**
 * DemoLimitPopup - Full-screen overlay for demo limit notifications
 *
 * Features:
 * - Shows when free user hits attempt limit (5 attempts), question limit (2 questions),
 *   or session limit (all sessions used)
 * - For attempt_limit: "Continue" button dismisses popup, session remains open for next question
 * - For question_limit / session_limit: "Back to Sessions" button navigates to /sessions
 * - No auto-dismiss (user must click the button)
 * - Accessible: role="dialog", aria-modal="true", focus management, focus trap, Escape key dismiss
 *
 * @example
 * ```tsx
 * <DemoLimitPopup
 *   isVisible={demoPopupReason !== null}
 *   reason={demoPopupReason ?? 'attempt_limit'}
 *   onDismiss={dismissDemoPopup}
 * />
 * ```
 */
export function DemoLimitPopup({ isVisible, reason, onDismiss, maxAttemptsPerQuestion, maxQuestionsPerSession, maxSessions, pebblesAwarded }: DemoLimitPopupProps) {
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const variant = getVariantContent({
    maxAttemptsPerQuestion: maxAttemptsPerQuestion ?? 5,
    maxQuestionsPerSession: maxQuestionsPerSession ?? 2,
    maxSessions: maxSessions ?? 2,
    pebblesAwarded: pebblesAwarded ?? 2,
  })[reason];

  // Auto-focus the action button when popup appears (AC-006.7 focus management)
  useEffect(() => {
    if (isVisible) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        buttonRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  // Handle dismiss: call onDismiss callback and navigate if needed
  const handleDismiss = useCallback(() => {
    onDismiss();
    if (variant.navigateAway) {
      navigate('/sessions');
    }
  }, [onDismiss, variant.navigateAway, navigate]);

  // Keyboard handling: Escape to dismiss, Tab trap to keep focus on button (AC-006.7)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleDismiss();
      }
      // Focus trap: only one interactive element (the button), keep Tab on it
      if (e.key === 'Tab') {
        e.preventDefault();
        buttonRef.current?.focus();
      }
    },
    [handleDismiss]
  );

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  return (
    /* Overlay - same pattern as CelebrationPopup */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-label={variant.heading}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {/* Centered card */}
      <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 text-center max-w-sm mx-4">
        {/* Header emoji (variant-specific) */}
        <div className="text-3xl md:text-4xl mb-3" aria-hidden="true">
          {variant.emoji}
        </div>

        {/* Heading */}
        <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{variant.heading}</h2>

        {/* Subtitle (demo variants only) */}
        {variant.subtitle && <p className="text-sm text-gray-500 mb-3">{variant.subtitle}</p>}

        {/* Reason-specific body message */}
        <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-6">{variant.body}</p>

        {/* Action button */}
        <button
          ref={buttonRef}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleDismiss();
          }}
          className="px-6 py-2 bg-pebbles-500 hover:bg-pebbles-600 text-white font-semibold rounded-lg transition-colors"
        >
          {variant.buttonLabel}
        </button>
      </div>
    </div>
  );
}
